import { IShippingAddress, ShippingAddress } from '@lib/address';
import { FileResponse, IFileResponse } from '@lib/core';
import { DemandProduct, IDemandProduct } from './DemandProduct';
import { DemandArticle } from './DemandArticle';
import { DemandPrice, IDemandPrice } from './DemandPrice';
import { DemandShippingAddressChange, DemandShippingAddressOrigin } from './DemandShippingAddressChange';
import { DemandDataService } from '../demand-data.service';
import { UnableToRetreiveDemandError } from './error/UnableToRetreiveDemandError';
import { HttpErrorResponse } from '@angular/common/http';
import { IDemandProductChangeResponse } from './IDemandProductChangeResponse';
import { DemandSupplier, IDemandSupplier } from './IDemandSupplierContact';
import { IPerson } from '@lib/user';
import { Logger, SortingService } from '@lib/util';
import dayjs, { Dayjs } from 'dayjs';
import { CommentDataService, DocumentCommentEntry } from '@lib/comment';
import { ItemClassificationDataService } from '@lib/classification';
import { FileDataService } from '@lib/file';
import { ItemAccountingDataService } from '@lib/accounting';
import { ITransmissionValue } from '@lib/client';
import { firstValueFrom } from 'rxjs';
import { DemandSendToSupplierStatus } from '../display/content/types/DemandSendStatus';
import { DemandSendToInternalErpStatus } from '../display/content/types/DemandSendToInternalErpStatus';
import { I18nService } from '@lib/i18n';
import {
    DemandItemAdditionalCost,
    DemandItemAdditionalCostInfo,
    IDemandItemAdditionalCost,
} from './IDemandItemAdditionalCost';
import { DemandAnnounceResult } from './IDemandAnnounceResult';
import { DemandValidationError } from '../validation/types/IDemandValidationError';

export class Demand {
    public id!: string;
    public price: DemandPrice[];
    public consumer?: IPerson;
    public additionalCosts: DemandItemAdditionalCost[] = [];
    public readOnly?: boolean;
    public transmissionInfo: ITransmissionValue = {
        preventTransferToSupplier: false,
        preventTransferToClientErp: false,
    };
    public validationError?: DemandValidationError;

    public onChange?: () => void;

    private _suppliers: DemandSupplier[];
    private _shippingAddress?: ShippingAddress;
    private _shippingAddressOrigin = DemandShippingAddressOrigin.USER;
    private _products: DemandProduct[];
    private _files: FileResponse[] = [];
    private announced = false;
    private _withdrawn = false;
    private _convertDirectlyToRfq = false;
    private _metadataChanged = false;
    private _validUntil?: Dayjs;
    private _messageToSupplier = '';
    private _internalMessage = '';
    private _conditions = '';
    private _title = '';
    private _companyCodeId: string | null = null;

    /**
     * Creates a DemandBasket instance. If a previous state has been saved to storage we will restore it.
     * If not, a new instance will be created
     * @param dataService
     * @param commentDataService
     * @param itemClassificationDataService
     * @param fileDataService
     * @param i18n
     */
    public static async current(
        dataService: DemandDataService,
        commentDataService: CommentDataService,
        itemClassificationDataService: ItemClassificationDataService,
        fileDataService: FileDataService,
        i18n: I18nService,
    ): Promise<Demand> {
        let demand: Demand;
        try {
            const dto: IDemand = await firstValueFrom(dataService.getNew());
            demand = Demand.fromDto(
                dto,
                dataService,
                commentDataService,
                itemClassificationDataService,
                fileDataService,
                i18n,
            );
        } catch (e) {
            if (e instanceof HttpErrorResponse) {
                throw new UnableToRetreiveDemandError(e);
            }
            // EPROC-11534: never swallow non-HTTP errors — returning the uninitialized
            // `demand` would hand `undefined as Demand` to the caller.
            throw e;
        }

        return demand;
    }

    public static async byId(
        demandId: string,
        dataService: DemandDataService,
        commentDataService: CommentDataService,
        itemClassificationDataService: ItemClassificationDataService,
        fileDataService: FileDataService,
        i18n: I18nService,
    ): Promise<Demand> {
        let demand: Demand;
        try {
            const demandDto: IDemand = await firstValueFrom(dataService.getById(demandId));
            demand = Demand.fromDto(
                demandDto,
                dataService,
                commentDataService,
                itemClassificationDataService,
                fileDataService,
                i18n,
            );
        } catch (e) {
            if (e instanceof HttpErrorResponse) {
                throw new UnableToRetreiveDemandError(e);
            }
            // EPROC-11534: never swallow non-HTTP errors — returning the uninitialized
            // `demand` would hand `undefined as Demand` to the caller.
            throw e;
        }

        return demand;
    }

    public static fromDto(
        dto: IDemand,
        dataService: DemandDataService,
        commentDataService: CommentDataService,
        itemClassificationDataService: ItemClassificationDataService,
        fileDataService: FileDataService,
        i18n: I18nService,
    ): Demand {
        const demand = new Demand(
            dto.id,
            dataService,
            commentDataService,
            itemClassificationDataService,
            fileDataService,
            i18n,
        );
        demand.apply(dto);

        return demand;
    }

    public get isAnnounced(): boolean {
        return this.announced;
    }

    public get currency(): string {
        return this.price.length > 0 ? this.price[0].currency : 'EUR';
    }

    get withdrawn(): boolean {
        return this._withdrawn;
    }

    get convertDirectlyToRfq(): boolean {
        return this._convertDirectlyToRfq;
    }

    public get valid(): boolean {
        return !!(
            this._products.length &&
            this._products.every((product) => product.valid) &&
            this._shippingAddress &&
            this.numOfCurrenciesUsed < 2
        );
    }

    public get validUntilDateValid(): boolean {
        return !!this._validUntil && this._validUntil.isValid() && this._validUntil.isAfter(dayjs());
    }

    public get validUntilDateAboutToBeInvalid(): boolean {
        return !!this._validUntil && this._validUntil.isBefore(dayjs().add(2, 'd'));
    }

    public get demandValidUntilIsInvalidInDays(): number | undefined {
        return this._validUntil?.startOf('d')?.diff(dayjs().startOf('d'), 'days');
    }

    public get products(): DemandProduct[] {
        return this._products;
    }

    public get files(): IFileResponse[] {
        return this._files.slice();
    }

    public get publicFiles(): IFileResponse[] {
        return this._files.filter((f) => !f.internal);
    }

    public get internalFiles(): IFileResponse[] {
        return this._files.filter((f) => f.internal);
    }

    public get shippingAddress(): ShippingAddress | undefined {
        return this._shippingAddress;
    }

    public get shippingAddressOrigin(): DemandShippingAddressOrigin {
        return this._shippingAddressOrigin;
    }

    /** Takes over the shipping address the server reports after a write that may have changed it. */
    public applyShippingAddressChange(change: DemandShippingAddressChange): void {
        this.takeOverShippingAddressChange(change);
        this.emitChange();
    }

    /**
     * Like {@link applyShippingAddressChange}, but without notifying listeners - for writes that are
     * part of a larger change which notifies once at its end ({@link addProduct}, {@link removeProduct}).
     */
    public takeOverShippingAddressChange(change: DemandShippingAddressChange): void {
        this._shippingAddress = change.shippingAddress;
        this._shippingAddressOrigin = change.origin;
    }

    public get hasSupplierArticleNumbers(): boolean {
        return this.products.some((p) => !!p.data.supplierArticleNumber);
    }

    public get numOfCurrenciesUsed(): number {
        const currencies = new Set<string>();
        this._products.map((p) => p.price.currency).forEach((currency) => currencies.add(currency));

        return currencies.size;
    }

    get title(): string {
        return this._title;
    }

    set title(value: string) {
        const isChanged = this._title !== value;
        this._title = value.trim();
        this._metadataChanged = isChanged;
    }
    get internalMessage(): string {
        return this._internalMessage;
    }

    set internalMessage(value: string) {
        const trimmedValue = value.trim();
        const isChanged = this._internalMessage !== trimmedValue;
        this._internalMessage = trimmedValue;
        this._metadataChanged = isChanged;
    }
    get messageToSupplier(): string {
        return this._messageToSupplier;
    }

    set messageToSupplier(value: string) {
        const trimmedValue = value.trim();
        const isChanged = this._messageToSupplier !== trimmedValue;
        this._messageToSupplier = trimmedValue;
        this._metadataChanged = isChanged;
    }
    get conditions(): string {
        return this._conditions;
    }

    set conditions(value: string) {
        const isChanged = this._conditions !== value;
        this._conditions = value;
        this._metadataChanged = isChanged;
    }

    public get hasIncotermDeviation(): boolean {
        return this._suppliers.some((supplier) => supplier.hasIncotermDeviation);
    }
    get validUntil(): Dayjs | undefined {
        return this._validUntil;
    }

    set validUntil(value: dayjs.Dayjs) {
        const isChanged = this._validUntil !== value;
        this._validUntil = value;
        this._metadataChanged = isChanged;
    }

    public get companyCodeId(): string | null {
        return this._companyCodeId;
    }

    constructor(
        id: string,
        private readonly dataService: DemandDataService,
        private readonly commentDataService: CommentDataService,
        private readonly itemClassificationDataService: ItemClassificationDataService,
        private readonly fileDataService: FileDataService,
        private readonly i18n: I18nService,
    ) {
        this._products = [];
        this._suppliers = [];
        this.price = [];
        this.id = id;
        fileDataService
            .getFiles(id)
            .subscribe((files: IFileResponse[]) => (this._files = files.map((f) => new FileResponse(f))));
    }

    public async refresh(): Promise<void> {
        const dto: IDemand = await firstValueFrom(this.dataService.getById(this.id));
        this.apply(dto);
    }

    /**
     * EPROC-11619: fetches accounting assignments and classification for every position in one
     * batched request each, instead of the O(N) cost of every position calling
     * `ItemAccountingDataService`/`ItemClassificationDataService` individually (see the previous,
     * per-position `DemandArticle.from()` loop this replaces). Deliberately passes only `this.id`
     * (the document id), not an item id list: a security review flagged an earlier version that sent
     * the item ids as an IDOR - the backend now resolves the item set itself, scoped to the caller's
     * company.
     */
    public async getProductsAsArticles(itemAccountingDataService: ItemAccountingDataService): Promise<DemandArticle[]> {
        const hasProducts = this._products.length > 0;
        const [accountingByItemId, classificationByItemId] = await Promise.all([
            hasProducts
                ? firstValueFrom(itemAccountingDataService.retrieveAccountAssignmentsForItems(this.id))
                : Promise.resolve({}),
            hasProducts
                ? firstValueFrom(this.itemClassificationDataService.getClassificationsForItems(this.id))
                : Promise.resolve({}),
        ]);

        return this._products.map((product) => {
            if (product.itemId) {
                product.applyClassification(classificationByItemId[product.itemId]);
            }
            const accounting = product.itemId ? accountingByItemId[product.itemId] : undefined;
            return DemandArticle.fromPrefetched(this.id, product, itemAccountingDataService, accounting);
        });
    }

    public getProduct(productId: string): DemandProduct | undefined {
        return this.products.find((p) => p.itemId === productId);
    }

    public hasProduct(productId: string): boolean {
        return !!this.products.find((p) => p.itemId === productId);
    }

    /**
     * Adds the position on the server. `completeNewProduct` runs before listeners are notified, so
     * data written to the new position afterwards (attachments, copied accounting, classification,
     * site) is already persisted when they rebuild their view of the positions.
     *
     * Once the position exists, follow-up failures of this method do not reject: a rejection would
     * look like a failed add, and saving again would create the position a second time. Errors thrown
     * by `completeNewProduct` itself are passed on.
     */
    public async addProduct(
        product: DemandProduct,
        completeNewProduct?: (newProduct: DemandProduct) => Promise<void>,
    ): Promise<DemandProduct | null> {
        if (!this.readOnly) {
            const response: IDemandProductChangeResponse = await firstValueFrom(
                this.dataService.addProduct(this.id, product),
            );
            const newProduct: DemandProduct = DemandProduct.fromDto(
                response.changedProduct,
                this.id,
                this.dataService,
                this.itemClassificationDataService,
                this.fileDataService,
            );
            this.price = response.demandPrice;
            this._products.push(newProduct);
            try {
                await this.refreshAdditionalCosts();
            } catch {
                // Reported by the HTTP error handler; the price from the add response stays in place.
            }
            try {
                await completeNewProduct?.(newProduct);
            } finally {
                this.emitChange();
            }
            return newProduct;
        }

        return null;
    }

    /**
     * Removes a position. Removing the last position of a plant can change the shipping address, so the
     * address the server reports is taken over and returned for the caller to announce a replacement.
     */
    public async removeProduct(productId: string): Promise<DemandShippingAddressChange | null> {
        if (this.readOnly) {
            return null;
        }
        const removed = await firstValueFrom(this.dataService.removeProduct(this.id, productId));
        this.price = removed.price;
        this._products = this._products.filter((p) => p.itemId !== productId);
        this.takeOverShippingAddressChange(removed.shippingAddress);
        try {
            await this.refreshAdditionalCosts();
        } catch {
            // The position is gone on the server, so this must not look like a failed removal. Reported by
            // the HTTP error handler; the price from the removal response stays in place.
        }
        this.emitChange();
        return removed.shippingAddress;
    }

    get suppliers(): DemandSupplier[] {
        return this._suppliers;
    }

    public async addSupplier(supplier: DemandSupplier): Promise<void> {
        if (!this.readOnly) {
            this._suppliers = SortingService.sortSortables(
                await firstValueFrom(
                    this.dataService.addSupplier(this.id, supplier.companyId.value, supplier.contact.id),
                ),
            );
            this.emitChange();
        }
    }

    public async addSuppliers(suppliers: DemandSupplier[]): Promise<void> {
        if (!this.readOnly) {
            for (const supplier of suppliers) {
                await this.addSupplier(supplier);
            }
            await this.refreshAdditionalCosts();
            this.emitChange();
        }
    }

    public async removeSupplier(supplierCompanyId: string): Promise<void> {
        if (!this.readOnly) {
            this._suppliers = await firstValueFrom(this.dataService.removeSupplier(this.id, supplierCompanyId));
            await this.refreshAdditionalCosts();
            this.emitChange();
        }
    }

    public async changeSupplierContact(supplierCompanyId: string, contactId: string): Promise<void> {
        if (!this.readOnly) {
            this._suppliers = SortingService.sortSortables(
                await firstValueFrom(
                    this.dataService.updateSupplierContact(this.id, supplierCompanyId, contactId),
                ),
            );
            this.emitChange();
        }
    }

    public updatePriceInfo(newPrice: IDemandPrice[]): void {
        if (!this.readOnly) {
            this.price = newPrice.map((price) => DemandPrice.fromDto(price));
        }
    }

    public async saveMetaData(): Promise<void> {
        if (
            !this.readOnly &&
            !this._withdrawn &&
            this.validUntilDateValid &&
            !this._convertDirectlyToRfq &&
            this._metadataChanged
        ) {
            return firstValueFrom(
                this.dataService.saveMetaData(
                    this.id,
                    this._title,
                    this._messageToSupplier,
                    this._internalMessage,
                    this._conditions,
                    this._validUntil,
                ),
            );
        }
    }

    public async updateShippingAddress(newShippingAddress: ShippingAddress): Promise<void> {
        let change: DemandShippingAddressChange;
        if (!newShippingAddress.isFreeAddress && typeof newShippingAddress.id !== 'undefined') {
            change = await firstValueFrom(this.dataService.changeShippingAddress(this.id, newShippingAddress.id));
        } else if (newShippingAddress.isFreeAddress && !newShippingAddress.id) {
            change = await firstValueFrom(this.dataService.changeShippingAddressToCustom(this.id, newShippingAddress));
        } else {
            const error = `no suitable operation found for address[isFreeAddress=${newShippingAddress.isFreeAddress},id=${newShippingAddress.id}] `;
            Logger.error('ADDRESS_CHANGE', error);
            throw new Error(error);
        }

        this.applyShippingAddressChange(change);
    }

    public async announce(): Promise<void> {
        if (!this.readOnly && !this.announced) {
            try {
                const result: DemandAnnounceResult = await firstValueFrom(this.dataService.announce(this.id));
                if (!result.error) {
                    this.announced = true;
                    this.validationError = undefined;
                } else {
                    this.validationError = result.error;
                }
            } catch {
                this.announced = false;
            } finally {
                this.emitChange();
            }
        }
    }

    public async addFile(formData: FormData, internal: boolean): Promise<void> {
        this._files = await firstValueFrom(this.fileDataService.addFile(formData, this.id, internal));
    }

    public async deleteFile(file: IFileResponse): Promise<void> {
        this._files = await firstValueFrom(this.fileDataService.deleteFile(file.id, this.id));
    }

    public async refreshAdditionalCosts(): Promise<void> {
        const additionalCostInfo: DemandItemAdditionalCostInfo = await firstValueFrom(
            this.dataService.getAdditionalCosts(this.id),
        );
        this.additionalCosts = additionalCostInfo.additionalCosts;
        this.price = additionalCostInfo.price;
    }

    public withdraw(): void {
        this._withdrawn = true;
        this.emitChange();
    }

    public convertToRfq(): void {
        this._convertDirectlyToRfq = true;
        this.emitChange();
    }

    public async addComment(comment: string): Promise<void> {
        try {
            await this.commentDataService.addComment(comment, this.id).toPromise();
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Sets the company code. It clears every position site, so the shipping address falls back to the
     * personal default; the address the server reports is taken over and returned.
     */
    public async updateCompanyCode(companyCodeId: string): Promise<DemandShippingAddressChange | null> {
        if (this.readOnly || !companyCodeId) {
            return null;
        }
        const normalized = companyCodeId.trim();
        if (!normalized || this._companyCodeId === normalized) {
            return null;
        }
        const change = await firstValueFrom(this.dataService.updateCompanyCode(this.id, normalized));
        this._companyCodeId = normalized;
        this.applyShippingAddressChange(change);
        return change;
    }

    public async getAllComments(): Promise<DocumentCommentEntry[]> {
        try {
            return firstValueFrom(this.commentDataService.getAllComments(this.id));
        } catch {
            return [];
        }
    }

    public async saveTransmissionInfo(): Promise<void> {
        await firstValueFrom(this.dataService.saveTransmissionIngo(this.id, this.transmissionInfo));
    }

    public async removeAllSupplierArticleNumbers(): Promise<void> {
        for (const product of this.products) {
            product.data.supplierArticleNumber = '';
            await product.save();
            this.emitChange();
        }
    }

    public async updateDeliveryDateForAllItems(desiredDeliveryDate: Dayjs): Promise<void> {
        if (this.readOnly) {
            return;
        }

        await firstValueFrom(this.dataService.updateDeliveryDateForAllItems(this.id, desiredDeliveryDate));
        await this.refresh();
        this.emitChange();
    }

    public setValidationError(validationError: DemandValidationError): void {
        this.validationError = validationError;
        this.emitChange();
    }

    private emitChange(): void {
        if (this.onChange) {
            this.onChange();
        }
    }

    private apply(dto: IDemand): void {
        this._products = dto.products.map((product: IDemandProduct) =>
            DemandProduct.fromDto(
                product,
                dto.id,
                this.dataService,
                this.itemClassificationDataService,
                this.fileDataService,
            ),
        );
        this._suppliers = SortingService.sortSortables(dto.suppliers.map((s) => DemandSupplier.instance(s, this.i18n)));
        this.price = dto.price.map((price) => DemandPrice.fromDto(price));
        this._shippingAddress = dto.shippingAddress ? ShippingAddress.from(dto.shippingAddress) : undefined;
        this._shippingAddressOrigin = dto.shippingAddressOrigin ?? DemandShippingAddressOrigin.USER;
        this._messageToSupplier = dto.messageToSupplier;
        this._internalMessage = dto.internalMessage;
        this._conditions = dto.conditions ?? '';
        this.additionalCosts = dto.additionalCosts.length
            ? dto.additionalCosts.map((cost) => new DemandItemAdditionalCost(cost))
            : [];
        SortingService.sort(this.additionalCosts, (cost) => cost.name.toLowerCase());
        this._title = dto.title;
        if (dto.validUntil && dayjs(dto.validUntil).isValid()) {
            this._validUntil = dayjs(dto.validUntil);
        }
        if (typeof this.readOnly === 'undefined') {
            this.readOnly = dto.readOnly;
        }
        this.announced = dto.announced;
        this.consumer = dto.consumer;
        this.transmissionInfo = {
            preventTransferToSupplier:
                dto.transmissionData.sendToSupplierStatus === DemandSendToSupplierStatus.UNWANTED,
            preventTransferToClientErp:
                dto.transmissionData.sendToInternalErpStatus === DemandSendToInternalErpStatus.UNWANTED,
        };
        this._companyCodeId = dto.companyCodeId ?? null;
    }
}

export interface IDemand {
    id: string;
    title: string;
    products: IDemandProduct[];
    additionalCosts: IDemandItemAdditionalCost[];
    shippingAddress?: IShippingAddress;
    shippingAddressOrigin?: DemandShippingAddressOrigin;
    suppliers: IDemandSupplier[];
    price: IDemandPrice[];
    validUntil: string;
    consumer?: IPerson;
    messageToSupplier: string;
    internalMessage: string;
    conditions?: string | null;
    announced: boolean;
    readOnly: boolean;
    transmissionData: IDemandErpTransmissionData;
    companyCodeId?: string | null;
}

export interface IDemandErpTransmissionData {
    sendToSupplierStatus: DemandSendToSupplierStatus;
    sendToInternalErpStatus: DemandSendToInternalErpStatus;
}
